import React, { useState, useRef } from 'react';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { validateFile } from '../utils/validation';
import { Button } from './ui/Button';
interface PhotoUploadProps {
  files: File[];
  onChange: (files: File[]) => void;
  maxFiles?: number;
}
export function PhotoUpload({
  files,
  onChange,
  maxFiles = 5
}: PhotoUploadProps) {
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (files.length + selectedFiles.length > maxFiles) {
      setError(`You can only upload a maximum of ${maxFiles} photos.`);
      return;
    }
    const validFiles: File[] = [];
    let validationError = null;
    for (const file of selectedFiles) {
      const errorMsg = validateFile(file);
      if (errorMsg) {
        validationError = errorMsg;
        break;
      }
      validFiles.push(file);
    }
    if (validationError) {
      setError(validationError);
    } else {
      setError(null);
      onChange([...files, ...validFiles]);
    }
    // Reset input so same file can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    onChange(newFiles);
  };
  return <div className="space-y-3" data-id="element-60">
      <div className="flex items-center justify-between" data-id="element-61">
        <label className="block text-sm font-medium text-gray-700" data-id="element-62">
          Upload Photos{' '}
          <span className="text-gray-400 font-normal" data-id="element-63">(Optional)</span>
        </label>
        <span className="text-xs text-gray-500" data-id="element-64">
          {files.length}/{maxFiles}
        </span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-id="element-65">
        {files.map((file, index) => <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 group" data-id="element-66">
            <img src={URL.createObjectURL(file)} alt={`Upload ${index + 1}`} className="w-full h-full object-cover" data-id="element-67" />
            <button type="button" onClick={() => removeFile(index)} className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 hover:bg-red-500 transition-colors" data-id="element-68">
              <X className="w-3 h-3" data-id="element-69" />
            </button>
          </div>)}

        {files.length < maxFiles && <button type="button" onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-500 hover:border-teal-500 hover:text-teal-600 hover:bg-teal-50 transition-all" data-id="element-70">
            <Upload className="w-5 h-5 mb-1" data-id="element-71" />
            <span className="text-xs font-medium" data-id="element-72">Add</span>
          </button>}
      </div>

      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple className="hidden" onChange={handleFileSelect} data-id="element-73" />

      {error && <p className="text-sm text-red-600" data-id="element-74">{error}</p>}
      {!error && <p className="text-xs text-gray-500" data-id="element-75">
          Max 5 files. JPG, PNG, HEIC, WebP. Up to 8MB each.
        </p>}
    </div>;
}